/**
 * Usage tracker for AI sessions
 * Tracks tokens, audio time, and costs locally and syncs with backend
 */

export interface SessionUsage {
  sessionId: string;
  startTime: Date;
  endTime?: Date;
  inputTokens: number;
  outputTokens: number;
  inputAudioSeconds: number;
  outputAudioSeconds: number;
  turnsCount: number;
  feature?: string;
  model: string;
}

export interface UsageUpdate {
  tokens?: number;
  audioSeconds?: number;
  type: "input" | "output";
}

export class AIUsageTracker {
  private currentSession: SessionUsage | null = null;
  private sessionStartTime: number = 0;
  private lastSyncTime: number = 0;
  private syncInterval: number = 30000; // Sync every 30 seconds
  private syncTimer: number | null = null;

  constructor(
    private apiUrl: string,
    private getAuthToken: () => Promise<string | null>
  ) {}

  /**
   * Start tracking a new AI session
   */
  startSession(sessionId: string, feature?: string, model = "gemini-2.0-flash-live") {
    // End previous session if exists
    if (this.currentSession) {
      this.endSession();
    }

    this.currentSession = {
      sessionId,
      startTime: new Date(),
      inputTokens: 0,
      outputTokens: 0,
      inputAudioSeconds: 0,
      outputAudioSeconds: 0,
      turnsCount: 0,
      feature,
      model,
    };

    this.sessionStartTime = Date.now();
    this.lastSyncTime = Date.now();

    // Start periodic sync
    this.syncTimer = window.setInterval(() => {
      this.syncUsage();
    }, this.syncInterval);
  }

  /**
   * Update usage metrics during session
   */
  updateUsage(update: UsageUpdate) {
    if (!this.currentSession) return;

    if (update.type === "input") {
      if (update.tokens) this.currentSession.inputTokens += update.tokens;
      if (update.audioSeconds) this.currentSession.inputAudioSeconds += update.audioSeconds;
    } else {
      if (update.tokens) this.currentSession.outputTokens += update.tokens;
      if (update.audioSeconds) this.currentSession.outputAudioSeconds += update.audioSeconds;
    }
  }

  /**
   * Increment turn count
   */
  incrementTurn() {
    if (!this.currentSession) return;
    this.currentSession.turnsCount++;
  }

  /**
   * End the current session and sync final usage
   */
  async endSession() {
    if (!this.currentSession) return;

    this.currentSession.endTime = new Date();

    // Clear sync timer
    if (this.syncTimer) {
      window.clearInterval(this.syncTimer);
      this.syncTimer = null;
    }

    // Final sync
    await this.syncUsage(true);

    this.currentSession = null;
  }

  /**
   * Sync usage data with backend
   */
  private async syncUsage(isFinal = false) {
    if (!this.currentSession) return;

    const token = await this.getAuthToken();
    if (!token) {
      console.warn("No auth token available for usage sync");
      return;
    }

    const durationSeconds = (Date.now() - this.sessionStartTime) / 1000;

    try {
      const response = await fetch(`${this.apiUrl}/api/v1/usage/track`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: this.currentSession.sessionId,
          input_tokens: this.currentSession.inputTokens,
          output_tokens: this.currentSession.outputTokens,
          input_audio_seconds: this.currentSession.inputAudioSeconds,
          output_audio_seconds: this.currentSession.outputAudioSeconds,
          model: this.currentSession.model,
          feature: this.currentSession.feature,
          duration_seconds: durationSeconds,
          turns_count: this.currentSession.turnsCount,
        }),
      });

      if (!response.ok) {
        console.error("Failed to sync usage:", await response.text());
      } else {
        this.lastSyncTime = Date.now();

        // Reset counters after successful sync (unless it's final)
        if (!isFinal) {
          this.currentSession.inputTokens = 0;
          this.currentSession.outputTokens = 0;
          this.currentSession.inputAudioSeconds = 0;
          this.currentSession.outputAudioSeconds = 0;
        }
      }
    } catch (error) {
      console.error("Error syncing usage:", error);
    }
  }

  /**
   * Get current session usage
   */
  getCurrentUsage(): SessionUsage | null {
    return this.currentSession ? { ...this.currentSession } : null;
  }

  /**
   * Check quota status before starting session
   */
  async checkQuota(): Promise<{
    withinQuota: boolean;
    tokensRemaining: number;
    audioSecondsRemaining: number;
    percentUsed: number;
  } | null> {
    const token = await this.getAuthToken();
    if (!token) return null;

    try {
      const response = await fetch(`${this.apiUrl}/api/v1/usage/check-quota`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error("Error checking quota:", error);
    }

    return null;
  }

  /**
   * Get current month usage
   */
  async getCurrentMonthUsage(): Promise<any> {
    const token = await this.getAuthToken();
    if (!token) return null;

    try {
      const response = await fetch(`${this.apiUrl}/api/v1/usage/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error("Error fetching usage:", error);
    }

    return null;
  }
}
